/**
 * MainMenuScene — Main menu with HTML/CSS overlay.
 * Buttons: New Game, Load Game, Settings, Quit.
 * DOM elements over PixiJS canvas with focus navigation support.
 */
import { Container, Text } from 'pixi.js';
import debugLog from '../debug/DebugLog.js';
import sceneManager from '../core/SceneManager.js';
import focusManager from '../input/FocusManager.js';

export default class MainMenuScene {
  constructor() {
    this.container = new Container();
    this._overlay = null;
    this._bgText = null;
  }

  async init() {
    debugLog.info('MainMenuScene: init');
  }

  async enter() {
    debugLog.event('MainMenuScene: enter');

    // PixiJS background decorative text
    this._bgText = new Text({
      text: 'Wanderlight',
      style: {
        fontFamily: 'Georgia, serif',
        fontSize: 72,
        fill: 0x1a1a2e,
        align: 'center',
      },
    });
    this._bgText.anchor.set(0.5);
    this.container.addChild(this._bgText);
    this._centerBgText();

    // HTML overlay for menu
    this._createOverlay();
  }

  _centerBgText() {
    const canvas = document.getElementById('game-canvas');
    const w = canvas ? canvas.clientWidth : 800;
    const h = canvas ? canvas.clientHeight : 600;
    if (this._bgText) {
      this._bgText.x = w / 2;
      this._bgText.y = h / 3;
    }
  }

  _createOverlay() {
    const uiRoot = document.getElementById('ui-root');

    this._overlay = document.createElement('div');
    this._overlay.id = 'main-menu-overlay';
    this._overlay.className = 'main-menu-overlay';

    // Title
    const title = document.createElement('h1');
    title.className = 'main-menu-title';
    title.textContent = 'Wanderlight';
    this._overlay.appendChild(title);

    // Subtitle
    const subtitle = document.createElement('p');
    subtitle.className = 'main-menu-subtitle';
    subtitle.textContent = 'A Caravan RPG by Waystone Interactive';
    this._overlay.appendChild(subtitle);

    // Button container
    const btnContainer = document.createElement('div');
    btnContainer.className = 'main-menu-buttons';

    const buttons = [
      { id: 'btn-new-game', label: 'New Game', index: 0 },
      { id: 'btn-load-game', label: 'Load Game', index: 1 },
      { id: 'btn-settings', label: 'Settings', index: 2 },
      { id: 'btn-quit', label: 'Quit', index: 3 },
    ];

    for (const btnDef of buttons) {
      const btn = document.createElement('button');
      btn.id = btnDef.id;
      btn.className = 'main-menu-btn';
      btn.textContent = btnDef.label;
      btn.setAttribute('data-focusable', 'true');
      btn.setAttribute('data-focus-index', String(btnDef.index));

      btn.addEventListener('click', () => {
        debugLog.event(`MainMenu: "${btnDef.label}" clicked`);
        if (btnDef.id === 'btn-new-game') {
          sceneManager.transition('CharCreate');
        } else {
          debugLog.info(`MainMenu button "${btnDef.label}" action not yet implemented.`);
        }
      });

      btnContainer.appendChild(btn);
    }

    this._overlay.appendChild(btnContainer);

    // Version
    const version = document.createElement('div');
    version.className = 'main-menu-version';
    version.textContent = 'v0.1.0 — Milestone 1';
    this._overlay.appendChild(version);

    uiRoot.appendChild(this._overlay);

    // Push focus group for keyboard/gamepad navigation
    focusManager.pushGroup('main-menu', this._overlay);
  }

  exit() {
    debugLog.event('MainMenuScene: exit');
    // Clear focus groups
    focusManager.clear();
    // Remove HTML overlay
    if (this._overlay && this._overlay.parentNode) {
      this._overlay.parentNode.removeChild(this._overlay);
      this._overlay = null;
    }
  }

  update(dt) {
    // No update logic needed for main menu
  }

  resize(w, h) {
    this._centerBgText();
  }
}
